import * as React from "react";
import {
    Box,
    Stack,
    Tooltip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";

const COLUMNS = [
    { key: "run_id", label: "run_id", align: "center", width: 140 },
    { key: "sample_id", label: "sample_id", align: "center", width: 160 },
    { key: "ORF_ID", label: "ORF_ID", align: "left", width: 520 },
    { key: "Contig", label: "Contig", align: "left", width: 260 },
    { key: "Start", label: "Start", align: "center", width: 110 },
    { key: "Stop", label: "Stop", align: "center", width: 110 },
    { key: "Orientation", label: "Orientation", align: "center", width: 120 },
    { key: "Cut_Off", label: "Cut_Off", align: "center", width: 120 },
    { key: "Pass_Bitscore", label: "Pass_Bitscore", align: "center", width: 140 },
    { key: "Best_Hit_Bitscore", label: "Best_Hit_Bitscore", align: "center", width: 170 },
    { key: "Best_Hit_ARO", label: "Best_Hit_ARO", align: "center", width: 170 },
    { key: "Best_Identities", label: "Best_Identities", align: "center", width: 150 },
    { key: "ARO", label: "ARO", align: "center", width: 120 },
    { key: "Model_type", label: "Model_type", align: "center", width: 220 },
    { key: "SNPs_in_Best_Hit_ARO", label: "SNPs_in_Best_Hit_ARO", align: "center", width: 200 },
    { key: "Other_SNPs", label: "Other_SNPs", align: "center", width: 140 },
    { key: "Drug Class", label: "Drug Class", align: "left", width: 420 },
    { key: "Resistance Mechanism", label: "Resistance Mechanism", align: "left", width: 220 },
    { key: "AMR Gene Family", label: "AMR Gene Family", align: "left", width: 320 },
    {
        key: "Percentage Length of Reference Sequence",
        label: "Percentage Length of Reference Sequence",
        align: "center",
        width: 280,
    },
    { key: "Antibiotic", label: "Antibiotic", align: "left", width: 420 },
];

// --------- Util CSV
const escapeCSV = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    const mustQuote = /[",\n\r]/.test(str);
    const escaped = str.replace(/"/g, '""');
    return mustQuote ? `"${escaped}"` : escaped;
};

const rowsToCSV = (rows, columns) => {
    const header = columns.map((c) => c.label);
    const body = (rows ?? []).map((r) => columns.map((c) => escapeCSV(r?.[c.key] ?? "")));
    return [header.map(escapeCSV), ...body].map((arr) => arr.join(",")).join("\r\n");
};

function ToolbarCSV({ rows, columns, filename = "rgi_results.csv" }) {
    const csv = React.useMemo(() => rowsToCSV(rows ?? [], columns), [rows, columns]);

    const copyCSV = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(csv);
                return;
            }
        } catch {
            // fallback
        }

        const ta = document.createElement("textarea");
        ta.value = csv;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
    };

    const downloadCSV = () => {
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Tooltip title="Copiar CSV">
                <IconButton size="small" onClick={copyCSV}>
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Descargar CSV">
                <IconButton size="small" onClick={downloadCSV}>
                    <DownloadIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

export default function RGIResultsTable({
    rows = [],
    emphasizeRow = (r) => r?.Cut_Off === "Perfect",
    height = "auto",
}) {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const paginatedRows = (rows ?? []).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const containerRef = React.useRef(null);
    const [rowHeight, setRowHeight] = React.useState(null);

    React.useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const HEADER_HEIGHT = 114;
        const MIN_ROW_HEIGHT = 48;

        const recalc = () => {
            if (height === "auto") {
                setRowHeight(null);
                return;
            }

            const containerHeight = el.clientHeight;
            const bodyHeight = Math.max(containerHeight - HEADER_HEIGHT, 0);
            const n = Math.max(paginatedRows.length, 1);
            const next = Math.max(Math.floor(bodyHeight / n), MIN_ROW_HEIGHT);
            setRowHeight(next);
        };

        recalc();
        const ro = new ResizeObserver(recalc);
        ro.observe(el);
        return () => ro.disconnect();
    }, [paginatedRows.length, rowsPerPage, page, height]);

    return (
        <Box sx={{ width: "100%" }}>
            <ToolbarCSV rows={rows} columns={COLUMNS} filename="rgi_results.csv" />

            <Paper
                elevation={0}
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    height: 700,
                    width: "100%",
                    maxWidth: "100%",
                }}
            >
                <TableContainer
                    ref={containerRef}
                    sx={{
                        width: '100%'
                    }}
                >
                    <Table
                        stickyHeader
                        size="small"
                        aria-label="RGI results"
                        sx={{
                            width: "max-content",
                            "& th": {
                                bgcolor: "#000",
                                color: "#fff",
                                fontWeight: 700,
                                height: "100px",
                                whiteSpace: "nowrap",
                            },
                            "& td, & th": { borderColor: "divider" },
                        }}
                    >
                        {/* Mantén colgroup para que la tabla tenga ancho total real */}
                        <colgroup>
                            {COLUMNS.map((c) => (
                                <col key={c.key} style={{ width: c.width }} />
                            ))}
                        </colgroup>

                        <TableHead>
                            <TableRow>
                                {COLUMNS.map((c) => (
                                    <TableCell key={c.key} align={c.align}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} align="center">
                                            {c.label}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paginatedRows.map((r, idx) => {
                                const bold = emphasizeRow?.(r);
                                const key = `${r?.sample_id ?? "na"}|${r?.ORF_ID ?? "na"}|${page * rowsPerPage + idx}`;

                                return (
                                    <TableRow key={key} sx={{ height: rowHeight ?? "auto" }}>
                                        {COLUMNS.map((c) => {
                                            const value = r?.[c.key];
                                            const isLongText =
                                                c.key === "ORF_ID" ||
                                                c.key === "Drug Class" ||
                                                c.key === "AMR Gene Family" ||
                                                c.key === "Antibiotic" ||
                                                c.key === "Resistance Mechanism" ||
                                                c.key === "Contig";

                                            return (
                                                <TableCell
                                                    key={c.key}
                                                    align={c.align}
                                                    variant="body2"
                                                >
                                                    {value === null || value === undefined || value === "" ? "—" : String(value)}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    sx={{ mt: "auto" }}
                    count={(rows ?? []).length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Paper>
        </Box>
    );
}